import { launchBlocker } from './selection';

import type { BulkModule } from '../schemas/bulk-migration';

/**
 * The launch dialog's words and its submit rule, as plain data.
 *
 * Three things must all be true to submit, and each is the operator's own
 * statement rather than a default: how many (the count, within the cap), why
 * (a reason, which the runner stamps on every run it starts), and that they
 * mean it (the typed phrase the release turn-off already uses).
 *
 * A fourth is optional and OFF until ticked: re-syncing clients already
 * migrated, offered only on a module the runner says is safe to re-drive.
 */

export const MAX_REASON = 500;

function clients(count: number): string {
    return `${count.toLocaleString('en-US')} ${count === 1 ? 'client' : 'clients'}`;
}

export const RESYNC_OPTION_LABEL =
    'Also re-sync clients already migrated (bring Salesforce changes)';

export const RESYNC_OPTION_HELP =
    'Records already migrated are read from Salesforce again and updated; records the ' +
    'module protects, such as a brand edited in Veevart 2.0, are left as they are.';

/** Whether the dialog offers the re-sync checkbox for this module. */
export function resyncOffered(module: Pick<BulkModule, 'supportsResync'>): boolean {
    return module.supportsResync;
}

export interface LaunchConfirmView {
    readonly title: string;
    readonly sentence: string;
    readonly confirmLabel: string;
}

export function launchConfirmView(input: {
    readonly count: number;
    readonly moduleTitle: string;
    readonly environment: string;
    /** The checkbox is ticked (and offered). */
    readonly resync?: boolean;
}): LaunchConfirmView {
    const resync = input.resync === true;
    const verb = resync ? 'Migrate and re-sync' : 'Migrate';
    return {
        title: `${verb} ${input.moduleTitle} for ${clients(input.count)}`,
        sentence:
            `This starts a ${input.moduleTitle} migration from Salesforce into V2 for ` +
            `${clients(input.count)} in ${input.environment}. ` +
            (resync
                ? 'Records already migrated are read from Salesforce again and updated. '
                : '') +
            'It writes V2 data for each of them and cannot be undone from here.',
        confirmLabel: `${verb} ${clients(input.count)}`,
    };
}

/** What the launch sends. `resync` travels only when offered AND ticked. */
export interface LaunchInputView {
    readonly reason: string;
    readonly tenantIds: readonly string[];
    readonly resync?: true;
}

export function launchInput(input: {
    readonly reason: string;
    readonly tenantIds: readonly string[];
    readonly module: Pick<BulkModule, 'supportsResync'>;
    readonly resync: boolean;
}): LaunchInputView {
    return {
        reason: input.reason.trim(),
        tenantIds: input.tenantIds,
        ...(input.resync && resyncOffered(input.module) ? { resync: true as const } : {}),
    };
}

/** Why the dialog cannot submit yet, or `null` when it can. */
export function launchSubmitBlocker(input: {
    readonly count: number;
    readonly reason: string;
    readonly phraseMatches: boolean;
}): string | null {
    const count = launchBlocker(input.count);
    if (count) return count;
    const reason = input.reason.trim();
    if (reason.length === 0) return 'Say why — it is recorded on every run this starts.';
    if (reason.length > MAX_REASON) return `At most ${String(MAX_REASON)} characters.`;
    if (!input.phraseMatches) return 'Type the phrase to confirm.';
    return null;
}
